import { yupResolver } from "@hookform/resolvers/yup";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import AddressForm from "components/common/AddressForm";
import ContactInfoForm from "components/common/ContactInfoForm";
import type { CreatePracticeBranchDto } from "libs/cellera-sdk";
import { useCallback, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { branchSchema, type BranchFormData } from "validations";

interface AddBranchModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onAdd: (data: CreatePracticeBranchDto) => Promise<void>;
}

function AddBranchModal({ open, onClose, onAdd }: AddBranchModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const methods = useForm({
    resolver: yupResolver(branchSchema),
    defaultValues: {
      address: "",
      street: "",
      city: "",
      state: "",
      zip: "",
      emailAddress: "",
      primaryPhoneNumber: "",
      faxNumber: "",
    },
    mode: "onChange",
  });

  const handleSubmit = useCallback(
    async (data: BranchFormData) => {
      try {
        setIsSubmitting(true);
        await onAdd(data as CreatePracticeBranchDto);
        methods.reset();
        onClose();
      } catch (error) {
        console.error("Error adding branch:", error);
        // Error toast handled by parent
      } finally {
        setIsSubmitting(false);
      }
    },
    [onAdd, onClose, methods]
  );

  const handleClose = useCallback(() => {
    methods.reset();
    onClose();
  }, [onClose, methods]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Add a Branch</DialogTitle>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSubmit)}>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              {/* Address Section */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" className="font-medium mb-4">
                  Address
                </Typography>
                <AddressForm />
              </Box>

              {/* Contact Information Section */}
              <Box>
                <Typography variant="subtitle1" className="font-medium mb-4">
                  Contact Information
                </Typography>
                <ContactInfoForm />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!methods.formState.isValid || isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            >
              {isSubmitting ? "Adding..." : "Add"}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
}

export default AddBranchModal;
